import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { Breadcrumbs } from './Breadcrumbs';
import { SearchPalette } from './SearchPalette';
import { useSidebarState } from '@/hooks/use-sidebar-state';
import { cn } from '@/lib/utils';
import { BillingSetupBanner } from '@/features/billing/BillingSetupBanner';
import { GettingStartedHelp } from '@/components/layout/GettingStartedHelp';
import { useAuth } from '@/stores/use-auth';
import { getSidebarNavItems } from '@/lib/navigation-config';
import { useCurrentOrganization } from '@/hooks/use-organization';
import { useGovernanceStandardsWorkflowsGate } from '@/features/work-items/hooks/useGovernanceStandardsWorkflowsGate';
import { useInboxDocumentTitle } from '@/features/inbox/hooks/useInboxDocumentTitle';

interface DashboardLayoutProps {
  children: ReactNode;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { t } = useTranslation('layout');
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { isCollapsed, toggleSidebar } = useSidebarState();

  const eligibleForNextShell = useAuth(
    (s) => s.user?.nextNavigation?.eligibleForNextShell ?? false
  );
  const { organization } = useCurrentOrganization();
  const standardsGate = useGovernanceStandardsWorkflowsGate(organization?.organizationId);
  useInboxDocumentTitle(organization?.organizationId);
  const sidebarNavItems = useMemo(
    () =>
      getSidebarNavItems(eligibleForNextShell, {
        ...(standardsGate.data === false ? { governanceStandardsWorkflowsEnabled: false } : {}),
      }),
    [eligibleForNextShell, standardsGate.data]
  );

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsSearchOpen((open) => !open);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div
      className={cn(
        'flex h-screen overflow-hidden bg-background',
        eligibleForNextShell && 'next-shell-layout'
      )}
    >
      <Sidebar
        isMobileOpen={isMobileOpen}
        onMobileClose={() => setIsMobileOpen(false)}
        isCollapsed={isCollapsed}
        onToggleCollapse={toggleSidebar}
        navItems={sidebarNavItems}
      />
      <div className="relative flex min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden">
        <TopBar
          onMenuClick={() => setIsMobileOpen(true)}
          onSearchClick={() => setIsSearchOpen(true)}
        />
        <main
          aria-label={t('main.ariaLabel')}
          className={cn('min-w-0 flex-1', isCollapsed ? 'lg:ml-0' : 'lg:ml-0')}
        >
          <div className="mx-auto max-w-screen-2xl min-w-0 p-4 md:p-6 2xl:p-10">
            <Breadcrumbs className="mb-4" />
            <BillingSetupBanner />
            {children}
          </div>
        </main>
      </div>

      <SearchPalette isOpen={isSearchOpen} onOpenChange={setIsSearchOpen} />
      <GettingStartedHelp />
    </div>
  );
};
